<template>
  <div style="width: 100%">
    <v-card width="100%" class="ml-3 mr-3">
      <v-card-title>
        <v-btn
          fab
          text
          small
          color="grey darken-2"
          :disabled="currentDay === 0"
          @click="--currentDay"
        >
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        {{ scheduleDays[currentDay] | formatDate }}
        <v-btn
          fab
          text
          small
          color="grey darken-2"
          :disabled="currentDay === Object.keys(schedule).length - 1"
          @click="++currentDay"
        >
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-window v-model="currentDay" vertical reverse>
        <v-window-item v-for="(dailySchedule, date) in schedule" :key="date">
          <v-list width="100%">
            <div
              v-for="(scheduleToday, index) in dailySchedule"
              :key="scheduleToday._id"
            >
              <v-list-item
                v-if="
                  !scheduleToday.children || scheduleToday.children.length === 0
                "
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ scheduleToday.startTime | formatTime }} -
                    {{ scheduleToday.endTime | formatTime }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{
                      scheduleLocale === "en" && scheduleToday.title_en
                        ? scheduleToday.title_en
                        : scheduleToday.title
                    }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    color="primary"
                    dark
                    @click="showDetailView(scheduleToday)"
                  >
                    {{ $t("details") }}
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-list-group v-else>
                <template #activator>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ scheduleToday.startTime | formatTime }} -
                      {{ scheduleToday.endTime | formatTime }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{
                        scheduleLocale === "en" && scheduleToday.title_en
                          ? scheduleToday.title_en
                          : scheduleToday.title
                      }}
                    </v-list-item-subtitle>
                    <template v-if="scheduleToday.chairpersons">
                      <v-list-item-subtitle
                        v-for="chairperson in scheduleToday.chairpersons"
                        :key="chairperson.name"
                      >
                        {{ chairperson.name }}
                        <span v-if="chairperson.company">
                          ({{ chairperson.company }})
                        </span>
                      </v-list-item-subtitle>
                    </template>
                  </v-list-item-content>
                </template>
                <v-list-item
                  v-for="childSchedule in scheduleToday.children"
                  :key="childSchedule._id"
                >
                  <v-list-item-content class="ml-2">
                    <v-list-item-title>
                      {{ childSchedule.startTime | formatTime }} -
                      {{ childSchedule.endTime | formatTime }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-html="
                        scheduleLocale === 'en' && childSchedule.title_en
                          ? childSchedule.title_en
                          : childSchedule.title
                      "
                    >
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      color="primary"
                      dark
                      @click="showDetailView(childSchedule)"
                      v-html="$t('details')"
                    >
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list-group>
              <v-divider v-if="index < dailySchedule.length - 1"></v-divider>
            </div>
          </v-list>
        </v-window-item>
      </v-window>
    </v-card>
    <v-dialog v-model="detailView" width="unset">
      <event-detail
        @close="detailView = false"
        :selectedEvent="selectedEvent"
      ></event-detail>
    </v-dialog>
  </div>
</template>

<script>
import {
  pageTypeResource,
  eventScheduleGroupResource,
} from "./../api/resources";
import { i18n } from "../plugins/i18n";
import * as moment from "moment";
import EventDetail from "./EventDetail.vue";

export default {
  components: {
    EventDetail,
  },
  props: ["events"],
  async mounted() {
    moment.locale(this.scheduleLocale);
    const [pageTypeRes, scheduleGroupRes] = await Promise.all([
      pageTypeResource.get(),
      eventScheduleGroupResource.get(""),
    ]);
    void pageTypeRes;

    const scheduleGroupResults = Array.isArray(scheduleGroupRes.data)
      ? scheduleGroupRes.data
      : Array.isArray(scheduleGroupRes.data && scheduleGroupRes.data.results)
      ? scheduleGroupRes.data.results
      : [];
    const scheduleGroupsById = scheduleGroupResults.reduce((acc, group) => {
      if (group && group._id) {
        acc[group._id] = group;
      }
      return acc;
    }, {});

    const scheduleMap = {};
    const toMinutes = time => {
      if (!time || !time.includes(":")) {
        return -1;
      }
      const parts = time.split(":");
      if (parts.length < 2) {
        return -1;
      }
      const h = Number.parseInt(parts[0], 10);
      const m = Number.parseInt(parts[1], 10);
      if (Number.isNaN(h) || Number.isNaN(m)) {
        return -1;
      }
      return h * 60 + m;
    };
    const resolveCategory = event => {
      const categoryFromEvent = Array.isArray(event._category)
        ? event._category[0]
        : event._category;
      if (categoryFromEvent && categoryFromEvent._id) {
        return categoryFromEvent;
      }
      if (!event.categoryId) {
        return null;
      }
      return scheduleGroupsById[event.categoryId] || null;
    };
    const sortByTimeAndTitle = (event1, event2) => {
      const startDiff =
        toMinutes(event1.startTime) - toMinutes(event2.startTime);
      if (startDiff !== 0) {
        return startDiff;
      }
      const title1 = event1.title || event1.title_en || "";
      const title2 = event2.title || event2.title_en || "";
      const titleDiff = title1.localeCompare(title2, undefined, {
        sensitivity: "base",
      });
      if (titleDiff !== 0) {
        return titleDiff;
      }
      return toMinutes(event1.endTime) - toMinutes(event2.endTime);
    };
    this.events.forEach(event => {
      if (!scheduleMap.hasOwnProperty(event.date)) {
        scheduleMap[event.date] = [];
        this.scheduleDays.push(event.date);
      }
      scheduleMap[event.date].push(event);
    });

    for (let date in scheduleMap) {
      const dailyEvents = scheduleMap[date].map(event => ({ ...event }));
      dailyEvents.sort(sortByTimeAndTitle);

      const mergedDay = [];

      const categoryEvents = {};
      for (const event of dailyEvents) {
        if (!event.categoryId) {
          mergedDay.push(event);
          continue;
        }
        if (!categoryEvents[event.categoryId]) {
          categoryEvents[event.categoryId] = [];
        }
        categoryEvents[event.categoryId].push(event);
      }

      Object.keys(categoryEvents).forEach(categoryId => {
        const eventsInCategory = categoryEvents[categoryId].sort(
          sortByTimeAndTitle
        );
        if (!eventsInCategory.length) {
          return;
        }

        const resolvedCategory = resolveCategory(eventsInCategory[0]);
        if (!resolvedCategory) {
          mergedDay.push(...eventsInCategory);
          return;
        }

        const clusters = [];
        let currentCluster = null;

        eventsInCategory.forEach(event => {
          const start = toMinutes(event.startTime);
          const end = toMinutes(event.endTime);

          if (!currentCluster) {
            currentCluster = {
              start,
              end,
              events: [event],
            };
            return;
          }

          if (start <= currentCluster.end) {
            currentCluster.events.push(event);
            if (end > currentCluster.end) {
              currentCluster.end = end;
            }
            return;
          }

          clusters.push(currentCluster);
          currentCluster = {
            start,
            end,
            events: [event],
          };
        });

        if (currentCluster) {
          clusters.push(currentCluster);
        }

        clusters.forEach(cluster => {
          cluster.events.sort(sortByTimeAndTitle);

          mergedDay.push({
            _id: [
              "group",
              categoryId,
              cluster.events[0].startTime,
              cluster.events[cluster.events.length - 1].endTime,
              cluster.events[0]._id,
            ].join("-"),
            startTime: cluster.events[0].startTime,
            endTime: cluster.events[cluster.events.length - 1].endTime,
            title: resolvedCategory.title || resolvedCategory.title_en,
            title_en: resolvedCategory.title_en || resolvedCategory.title,
            categoryId,
            chairpersons: Array.isArray(resolvedCategory.chairpersons)
              ? resolvedCategory.chairpersons
              : [],
            children: cluster.events,
          });
        });
      });

      mergedDay.sort(sortByTimeAndTitle);
      scheduleMap[date] = mergedDay;
    }

    this.schedule = scheduleMap;
    const today = moment().format("YYYY-MM-DD");
    const todayIndex = this.scheduleDays.findIndex(day => day === today);
    this.currentDay = Math.max(todayIndex, 0);
  },
  data: () => ({
    currentDay: 0,
    schedule: {},
    scheduleDays: [],
    selectedEvent: {},
    detailView: false,
  }),
  computed: {
    scheduleLocale() {
      return i18n.locale;
    },
  },
  methods: {
    showDetailView(scheduleToday) {
      this.selectedEvent = scheduleToday;
      this.detailView = true;
    },
  },
};
</script>
